/**
 * Exact replica of Form W-7 (Rev. August 2019), page 1.
 *
 * Every coordinate, rule, checkbox and type size below was measured out of the
 * official fw7.pdf with pdfplumber — see w7-rules.ts (generated) and the
 * comments in w7-geometry.ts. Bold/italic runs follow the PDF's own font
 * census: the form sets body copy in HelveticaNeue Roman 8pt and switches to
 * Bd/It/BdIt mid-sentence, so the emphasis here is not stylistic, it is what
 * the official prints.
 *
 * Layout is absolute rather than flowed. The official is a fixed-layout page;
 * matching it means placing things, not nudging them.
 */
import {
  At,
  Arrow,
  ArrowKoz,
  Brace,
  Cb,
  Field,
  Marker,
  RULE_W,
  Rules,
  pt,
  useW7FontMetrics,
} from "./w7-layout";

type Props = {
  value?: any;
  onChange?: (patch: any) => void;
  disabled?: boolean;
};

const W7Page = ({ value = {}, onChange, disabled }: Props) => {
  // Measures the page's faces in this browser and re-renders once they are in.
  // Every `base` below is arithmetic off those metrics, so this has to run
  // before the numbers mean anything.
  useW7FontMetrics();

  const v = value || {};
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange?.({ [k]: e.target.value });

  /** Mirrors the paths the previous table read, so the submit payload that
   *  Step3 builds from w7FormValues comes out identical. */
  const ex = v?.data?.extracted_data ?? v?.extracted_data ?? {};
  /**
   * The passport's own object, as extracted.
   *
   * Why it is needed at all: get-itin-data returns `extracted_data` holding the
   * passport NESTED under "passport" *and* the foreign-address step's flat
   * city/state/country/zip keys on the same object -- so a plain `country` read
   * yields the ADDRESS country. A Canadian passport with an Albanian foreign
   * address is why 6a printed "Albania".
   *
   * Why `v` and not `ex`: Step3 builds this component's value as
   *   { ...extracted_data.passport, ...w7_form_extracted_data }
   * so the passport arrives already flattened onto `v`, plus a `passport` key
   * from w7_form_extracted_data. `v.data`/`v.extracted_data` never exist, so
   * `ex` is always {} in this flow and every read through it is dead. The
   * nested object is the value to trust: form edits are saved as FLAT keys, so
   * `v.country` can be overwritten by a line-4 edit while `v.passport.country`
   * stays as the passport was read.
   */
  const pp = (v?.passport ?? ex?.passport ?? {}) as Record<string, any>;
  const hasUsCompany =
    v?.data?.has_us_company === true || v?.data?.has_us_company === "true";
  const sex = String(v?.sex ?? "").toUpperCase();
  const isMale = sex === "M" || sex === "MALE";
  const isFemale = sex === "F" || sex === "FEMALE";
  /** 6d expiry. The previous form printed this as one value off
   *  date_of_expiry; it is passport data, so it can arrive on either
   *  the flattened values or the extracted block. */
  const expiry = v?.date_of_expiry ?? pp?.date_of_expiry ?? ex?.date_of_expiry ?? "";
  /** Sign-here / agent phone, shown with its dialling code as the official
   *  expects (e.g. +9719715615863).
   *
   *  Step2 now saves phone_number already prefixed, and older records are not,
   *  so the code is only prepended when it is actually missing — otherwise the
   *  newer records would print it twice. */
  const dialCode = String(v?.country_code ?? "").trim();
  const rawPhone = String(
    v?.signaturePhone ?? v?.phone_number ?? ex?.phone_number ?? ""
  ).trim();
  const signaturePhone =
    !rawPhone || !dialCode || rawPhone.startsWith(dialCode) || rawPhone.startsWith("+")
      ? rawPhone
      : `${dialCode}${rawPhone}`;

  return (
    <div
      id="pdf-content-page-1"
      className="w7-page"
      style={{ position: "relative" }}
    >
      <Rules />

      {/* ================= masthead: 36 -> 84, split at 122.4 / 489.6 ======= */}
      <At x={36} base={54.14} size={7}>
        Form
      </At>
      <At
        x={60.1}
        base={54.14}
        size={24} face="blkcn"
      >
        W-7
      </At>
      <At x={36} base={63.5} tz={0.92} size={7}>
        (Rev. August 2019)
      </At>
      <At x={36} base={73.5} tz={0.95} size={7}>
        Department of the Treasury
      </At>
      <At x={36} base={80.99} tz={0.95} size={7}>
        Internal Revenue Service
      </At>

      <At x={122.4} base={46.6} w={367.2} size={14} align="center" face="demi">
        Application for IRS Individual
      </At>
      <At x={122.4} base={60.1} w={367.2} size={14} align="center" face="demi">
        Taxpayer Identification Number
      </At>
      <Arrow x={164.88} base={69.64} />
      <At x={173.9} base={71.14} size={8} bold>
        For use by individuals who are not U.S. citizens or permanent residents.
      </At>
      <Arrow x={251.62} base={78.61} />
      <At x={260.7} base={80.11} size={8} bold>
        See separate instructions.
      </At>

      <At x={489.6} base={62.56} w={86.6} size={7} align="center">
        OMB No. 1545-0074
      </At>

      {/* ================= before you begin / application type ============= */}
      <At x={36} base={93.42} size={9} bold italic>
        An IRS individual taxpayer identification number (ITIN) is for U.S.
        federal tax purposes only.
      </At>
      <At x={36} base={105.92} size={9} bold>
        Before you begin:
      </At>
      <At x={36} base={116.42} size={9}>
        <span>• </span>
        <span style={{ fontWeight: 700, fontStyle: "italic" }}>
          Don’t submit{" "}
        </span>
        <span style={{ fontStyle: "italic" }}>
          this form if you have, or are eligible to get, a U.S. social security
          number (SSN).
        </span>
      </At>

      <At x={443.2} base={93.93} tz={0.94} size={9}>
        Application type (check one box):
      </At>
      <Cb x={453.6} y={97.3} checked={v?.applicationType?.apply !== false} />
      <At x={465.6} base={104.18} size={9}>
        Apply for a new ITIN
      </At>
      <Cb x={453.6} y={107.7} checked={!!v?.applicationType?.renew} />
      <At x={465.6} base={114.92} size={9}>
        Renew an existing ITIN
      </At>

      {/* ================= reason you're submitting ======================== */}
      <At x={36} base={130.63} w={542}
        size={9}
        nowrap={false}
        leading={10.8}
        style={{ textAlign: "justify" }}
      >
        <b>Reason you’re submitting Form W-7. </b>
        Read the instructions for the box you check. <b>Caution: </b>
        If you check box <b>b, c, d, e, f, </b>or{" "}
        <b>
          g, you must file a U.S. federal tax return with Form W-7 unless you
          meet one of the exceptions{" "}
        </b>
        (see instructions).
      </At>

      {/* a–h. Letters at 43.2, boxes at 52.4, text at 64.8; rows on the
          12pt grid except d->e, which is 24pt to clear the brace. */}
      <At x={43.2} base={153.71} size={8} bold>a</At>
      <Cb x={52.4} y={146} />
      <At x={64.8} base={153.71} size={8}>
        Nonresident alien required to get an ITIN to claim tax treaty benefit
      </At>

      <At x={43.2} base={165.71} size={8} bold>b</At>
      <Cb x={52.4} y={158} checked={!hasUsCompany} />
      <At x={64.8} base={165.71} size={8}>
        Nonresident alien filing a U.S. federal tax return
      </At>

      <At x={43.2} base={177.71} size={8} bold>c</At>
      <Cb x={52.4} y={170} />
      <At x={64.8} base={177.71} size={8}>
        U.S. resident alien{" "}
        <b>(based on days present in the United States) </b>
        filing a U.S. federal tax return
      </At>

      {/* the brace spanning d and e — measured ink box, see Brace */}
      <Brace x={212} y={185} w={5.9} h={42.5} />

      <At x={43.2} base={189.71} size={8} bold>d</At>
      <Cb x={52.4} y={182} />
      <At x={64.8} base={189.71} size={8}>
        Dependent of U.S. citizen/resident alien
      </At>
      <At x={223.2} base={189.54} size={8}>
        If <b>d,</b> enter relationship to U.S. citizen/resident alien (see
        instructions)
      </At>
      <Arrow x={468.24} base={188.04} />
      <Field
        x={478}
        base={189.0}
        w={98}
        h={10}
        value={v?.dependentRelation || ""}
        onChange={set("dependentRelation")}
        disabled={disabled}
      />

      <At x={43.2} base={213.71} size={8} bold>e</At>
      <Cb x={52.4} y={206} />
      <At x={64.8} base={213.71} size={8}>
        Spouse of U.S. citizen/resident alien
      </At>
      <At x={223.2} base={213.54} size={8}>
        If <b>d</b> or <b>e,</b> enter name and SSN/ITIN of U.S.
        citizen/resident alien (see instructions)
      </At>
      <Arrow x={514.17} base={212.04} />
      <Field
        x={524}
        base={213.0}
        w={52}
        h={10}
        value={v?.spouseName || ""}
        onChange={set("spouseName")}
        disabled={disabled}
      />

      <At x={43.2} base={237.71} size={8} bold>f</At>
      <Cb x={52.4} y={230} />
      <At x={64.8} base={237.71} size={8}>
        Nonresident alien student, professor, or researcher filing a U.S.
        federal tax return or claiming an exception
      </At>

      <At x={43.2} base={249.71} size={8} bold>g</At>
      <Cb x={52.4} y={242} />
      <At x={64.8} base={249.71} size={8}>
        Dependent/spouse of a nonresident alien holding a U.S. visa
      </At>

      <At x={43.2} base={261.71} size={8} bold>h</At>
      <Cb x={52.4} y={254} checked={hasUsCompany} />
      <At x={64.8} base={261.71} size={8}>
        Other (see instructions)
      </At>
      <Arrow x={149.7} base={260.21} />
      <Field
        x={159}
        base={261.0}
        w={417}
        h={10}
        value={
          v?.otherReasonText ??
          (hasUsCompany ? "Exception 1(a) - A Partnership Interest" : "")
        }
        onChange={set("otherReasonText")}
        disabled={disabled || !hasUsCompany}
      />

      {/* additional information for a and f */}
      <At x={36} base={273.21} size={8}>
        Additional information for <b>a</b> and <b>f</b>: Enter treaty country
      </At>
      <Arrow x={230.84} base={271.71} />
      <Field
        x={240}
        base={273.0}
        w={112}
        h={10}
        value={v?.treatyCountry || ""}
        onChange={set("treatyCountry")}
        disabled={disabled}
      />
      <At x={355.9} base={273.21} size={8}>
        and treaty article number
      </At>
      <Arrow x={447.66} base={271.71} />
      <Field
        x={457}
        base={273.0}
        w={119}
        h={10}
        value={v?.treatyArticle || ""}
        onChange={set("treatyArticle")}
        disabled={disabled}
      />

      {/* ================= Name ============================================ */}
      <At x={36} base={287.14} size={10} bold>Name</At>
      <At x={36} base={297.71} size={8}>(see instructions)</At>
      <At x={36} base={309.78} size={8}>Name at birth if</At>
      <At x={36} base={319.71} size={8}>different . .</At>
      <Arrow x={94.24} base={318.21} />

      <At x={112} base={283.71} size={8} bold>1a</At>
      <At x={125.49} base={283.71} size={8}>First name</At>
      <At x={277.6} base={283.71} size={8}>Middle name</At>
      <At x={414.4} base={283.71} size={8}>Last name</At>
      <Field x={112} base={295.0} w={160} value={v?.firstName ?? v?.first_name ?? pp?.name ?? ex?.name ?? ""} onChange={set("firstName")} disabled={disabled} />
      <Field x={277.6} base={295.0} w={130} value={v?.middleName ?? v?.middle_name ?? ""} onChange={set("middleName")} disabled={disabled} />
      <Field x={414.4} base={295.0} w={160} value={v?.lastName ?? v?.last_name ?? pp?.surname ?? ex?.surname ?? ""} onChange={set("lastName")} disabled={disabled} />

      <At x={112} base={307.71} size={8} bold>1b</At>
      <At x={125.78} base={307.71} size={8}>First name</At>
      <At x={277.6} base={307.71} size={8}>Middle name</At>
      <At x={414.4} base={307.71} size={8}>Last name</At>
      <Field x={112} base={319.0} w={160} value={v?.birthFirstName ?? ""} onChange={set("birthFirstName")} disabled={disabled} />
      <Field x={277.6} base={319.0} w={130} value={v?.birthMiddleName ?? ""} onChange={set("birthMiddleName")} disabled={disabled} />
      <Field x={414.4} base={319.0} w={160} value={v?.birthLastName ?? ""} onChange={set("birthLastName")} disabled={disabled} />

      {/* ================= Applicant's Mailing Address ===================== */}
      <At x={36} base={337.14} size={10} bold>Applicant’s</At>
      <At x={36} base={349.14} size={10} bold>Mailing</At>
      <At x={36} base={361.14} size={10} bold>Address</At>
      <At x={112} base={331.71} size={8} bold>2</At>
      <At x={125.34} base={331.71} size={8}>
        Street address, apartment number, or rural route number.{" "}
        <b>If you have a P.O. box, see separate instructions.</b>
      </At>
      <Field x={112} base={343.0} w={462} value={v?.address1 ?? "42 N Independence Blvd"} onChange={set("address1")} disabled={disabled} />
      {/* the official indents this run past the line number; a literal run of
          spaces cannot do it, because `white-space: nowrap` collapses them —
          it sits at the official's own x instead */}
      <At x={125.34} base={355.71} size={8}>
        City or town, state or province, and country. Include ZIP code or
        postal code where appropriate.
      </At>
      <Field
        x={112}
        base={367.0}
        w={462}
        value={v?.address2 ?? "New Castle, DE 19720"}
        onChange={set("address2")}
        disabled={disabled}
      />

      {/* ================= Foreign Address ================================= */}
      <At x={36} base={384.58} size={9} bold>Foreign (non-</At>
      <At x={36} base={395.38} size={9} bold>U.S.) Address</At>
      <At x={36} base={405.46} size={8}>(see instructions)</At>
      <At x={112} base={379.71} size={8} bold>3</At>
      <At x={125.34} base={379.71} size={8}>
        Street address, apartment number, or rural route number.{" "}
        <b>Don’t use a P.O. box number.</b>
      </At>
      <Field x={112} base={391.0} w={462} value={v?.foreignAddress ?? ex?.address ?? ""} onChange={set("foreignAddress")} disabled={disabled} />
      <At x={125.34} base={403.71} size={8}>
        City or town, state or province, and country. Include postal code where
        appropriate.
      </At>
      <Field x={112} base={415.0} w={462} value={v?.foreignCity ?? [ex?.city, ex?.state, ex?.country, ex?.zip_code].filter(Boolean).join(", ")} onChange={set("foreignCity")} disabled={disabled} />

      {/* ================= Birth Information =============================== */}
      <At x={36} base={430.14} size={10} bold>Birth</At>
      <At x={36} base={440.14} size={10} bold>Information</At>
      <At x={112} base={427.71} size={8}><b>4</b></At>
      <At x={124.9} base={427.85} tz={0.9} w={104} size={8}>
        Date of birth (month / day / year)
      </At>
      <Field x={124.9} base={440.0} w={100} h={10} value={v?.date_of_birth ?? pp?.date_of_birth ?? ex?.date_of_birth ?? ""} onChange={set("date_of_birth")} disabled={disabled} />

      <At x={234.4} base={427.71} w={104} size={8}>Country of birth</At>
      {/* pp, not ex: `ex.country` is the foreign-address country (see pp above),
          which would print the applicant's mailing country as their birthplace
          whenever no value has been saved yet. */}
      <Field x={234.4} base={440.0} w={102} value={v?.country ?? pp?.country ?? ""} onChange={set("country")} disabled={disabled} />
      <At x={342.4} base={427.71} size={8}>City and state or province (optional)</At>
      <Field x={342.4} base={440.0} w={130} value={v?.place_of_birth ?? ""} onChange={set("place_of_birth")} disabled={disabled} />

      <At x={479.2} base={427.71} size={8} bold>5</At>
      <Cb x={491.6} y={422} checked={isMale} onChange={() => onChange?.({ sex: "M" })} disabled={disabled} />
      <At x={504} base={429.21} size={8}>Male</At>
      <Cb x={491.6} y={433.5} checked={isFemale} onChange={() => onChange?.({ sex: "F" })} disabled={disabled} />
      <At x={504} base={440.21} size={8}>Female</At>

      {/* ================= Other Information =============================== */}
      <At x={36} base={457.31} size={10} bold>Other</At>
      <At x={36} base={469.31} size={10} bold>Information</At>
      <At x={112} base={451.71} size={8} bold>6a</At>
      <At x={125.49} base={451.71} size={8}>Country(ies) of citizenship</At>
      {/* 6a wants the country's NAME ("India") — not the 3-letter code and not
          the passport's descriptive nationality ("INDIAN").
          This has now been asked for all three ways: the descriptive value was
          the original behaviour, the code was requested after that, and the
          client has since confirmed the plain country name is what they want.
          `country` is the only one of the three that holds it; place_of_issue
          carries the code (it is what 6d "Issued by" prints) and nationality
          the demonym, so both are kept only as fallbacks for a passport that
          yields no country.
          Written back under its own key so a manual edit takes priority over
          the seed, and so `nationality` stays intact in the submit payload. */}
      <Field x={112} base={464.0} w={118}
        value={
          v?.citizenship ??
          // Upper-cased to match the rest of the sheet: every other value the
          // passport supplies is already caps ("JOHN DOE", "PUNE, MAHARASHTRA",
          // "IND") because the document prints it that way and extraction keeps
          // it. _get_country_name is the exception -- it returns "India" -- so
          // without this, 6a would be the only title-case entry on the form.
          // Applied to the SEED only: a value the user typed themselves is left
          // exactly as they typed it, so what is displayed always matches what
          // the submit payload carries.
          (
            // pp.country first: the nested passport object, which form edits
            // never touch. v.country next -- Step3 flattens the same value onto
            // v, and it is still a country NAME even if a line-4 edit has
            // changed it. The code is the last resort.
            //
            // `nationality` is deliberately absent: it holds the demonym
            // ("CANADIAN"), never the country name the client asked for, and
            // when it sat ahead of the fallbacks it won and printed exactly
            // that.
            pp?.country ??
            v?.country ??
            pp?.place_of_issue ??
            v?.place_of_issue ??
            ""
          ).toUpperCase()
        }
        onChange={set("citizenship")} disabled={disabled} />
      <At x={234.4} base={451.71} size={8} bold>6b</At>
      <At x={245.96} base={451.71} size={8}>Foreign tax I.D. number (if any)</At>
      <Field x={234.4} base={464.0} w={130} value={v?.taxId ?? "N/A"} onChange={set("taxId")} disabled={disabled} />
      <At x={371.2} base={451.71} size={8} bold>6c</At>
      <At x={384.69} base={451.71} size={8} tz={0.95}>
        Type of U.S. visa (if any), number, and expiration date
      </At>
      <Field x={371.2} base={464.0} w={203} value={v?.visaInfo ?? "N/A"} onChange={set("visaInfo")} disabled={disabled} />

      <At x={112} base={477.21} size={8} bold>6d</At>
      <At x={125.79} base={477.21} size={8}>
        Identification document(s) submitted (see instructions)
      </At>
      <Cb x={333.2} y={470} checked={v?.doc === "passport" || !v?.doc} />
      <At x={345.6} base={477.21} size={8}>Passport</At>
      <Cb x={398} y={470} checked={v?.doc === "license"} />
      <At x={410.4} base={477.21} size={8}>Driver’s license/State I.D.</At>

      <Cb x={131.6} y={482} checked={v?.doc === "uscis"} />
      <At x={144} base={489.21} size={8}>USCIS documentation</At>
      <Cb x={239.6} y={482} checked={v?.doc === "other"} />
      <At x={252} base={489.21} size={8}>Other</At>
      <Field x={280} base={490.0} w={150} h={10} value={v?.otherDocsDescription ?? ""} onChange={set("otherDocsDescription")} disabled={disabled} />

      <At x={439.2} base={494.0} size={8}>Date of entry into</At>
      <At x={439.2} base={503.6} size={8}>the United States</At>
      <At x={439.2} base={513.2} size={8}>(MM/DD/YYYY):</At>
      <At x={521.79} base={513.71} size={8}>/</At>
      <At x={538.37} base={513.71} size={8}>/</At>

      <At x={129.6} base={513.21} size={8}>Issued by:</At>
      <Field x={170} base={514.0} w={52} h={10} value={v?.issuedBy ?? v?.place_of_issue ?? ""} onChange={set("issuedBy")} disabled={disabled} />
      <At x={226.2} base={513.71} size={8}>No.:</At>
      <Field x={245} base={514.0} w={80} h={10} value={v?.documentNo ?? v?.passport_number ?? pp?.passport_number ?? ex?.passport_number ?? ""} onChange={set("documentNo")} disabled={disabled} />
      <At x={327.9} base={513.71} size={8}>Exp. date:</At>
      {/* The official's two separators are drawn only while the field is
          blank — a filled date carries its own slashes and would
          otherwise print straight over them. */}
      {!expiry && (
        <>
          <At x={382.6} base={513.71} size={8}>/</At>
          <At x={399.2} base={513.71} size={8}>/</At>
        </>
      )}
      <Field x={371} base={514.0} w={62} h={10} value={expiry} onChange={set("date_of_expiry")} disabled={disabled} />

      <At x={112} base={525.71} size={8} bold>6e</At>
      <At x={125.49} base={525.71} size={8}>
        Have you previously received an ITIN or an Internal Revenue Service
        Number (IRSN)?
      </At>
      <Cb x={129.6} y={529.8} checked={v?.received !== true} />
      <At x={144} base={537.21} size={8}>
        <b>No/Don’t know. </b>Skip line 6f.
      </At>
      <Cb x={129.6} y={541.5} checked={v?.received === true} />
      <At x={144} base={548.71} size={8}>
        <b>Yes.</b> Complete line 6f. If more than one, list on a sheet and
        attach to this form (see instructions).
      </At>

      <At x={112} base={561.71} size={8} bold>6f</At>
      <At x={125.78} base={561.71} size={8}>Enter ITIN and/or IRSN</At>
      <Arrow x={209.93} base={560.21} />
      <At x={226.2} base={561.71} size={8} bold>ITIN</At>
      <At x={396} base={561.71} size={8} bold>IRSN</At>
      <At x={562.1} base={561.71} size={8}>and</At>

      {/* 6f ITIN / IRSN digit cells — 12 x 11pt each, measured positions */}
      {[
        244.8, 259.2, 273.6, 295.2, 309.6, 331.2, 345.6, 360.0, 374.4,
        417.6, 432.0, 446.4, 468.0, 482.4, 504.0, 518.4, 532.8, 547.2,
      ].map((x) => (
        <div
          key={x}
          style={{
            position: "absolute",
            left: pt(x),
            top: pt(553.5),
            width: pt(12),
            height: pt(11),
            border: RULE_W,
          }}
        />
      ))}
      <At x={286.5} base={561.0} size={7}>—</At>
      <At x={322.5} base={561.0} size={7}>—</At>
      <At x={459.3} base={561.0} size={7}>—</At>
      <At x={496.1} base={561.0} size={7}>—</At>
      <At x={128} base={574.71} size={8}>name under which it was issued</At>
      <Arrow x={244.76} base={573.21} />
      <At x={280} base={583.71} size={8}>First name</At>
      <At x={372.8} base={583.71} size={8}>Middle name</At>
      <At x={492.5} base={583.71} size={8}>Last name</At>

      <At x={112} base={596.71} size={8} bold>6g</At>
      <At x={125.78} base={596.71} size={8}>
        Name of college/university or company (see instructions)
      </At>
      <ArrowKoz x={330.4} base={596.71} />
      <Field x={340} base={597.0} w={234} h={10} value={v?.collegeName ?? ""} onChange={set("collegeName")} disabled={disabled} />
      <At x={125.5} base={609.21} size={8}>City and state</At>
      <Arrow x={179.59} base={607.71} />
      <Field x={190} base={610.0} w={175} h={10} value={v?.collegeCity ?? ""} onChange={set("collegeCity")} disabled={disabled} />
      <At x={369.5} base={609.21} size={8}>Length of stay</At>
      <ArrowKoz x={422.68} base={609.21} />
      <Field x={433} base={610.0} w={141} h={10} value={v?.lengthOfStay ?? ""} onChange={set("lengthOfStay")} disabled={disabled} />

      {/* ================= Sign Here ======================================= */}
      <At x={36} base={627.74} size={14} face="demi">Sign</At>
      <At x={36} base={644.54} size={14} face="demi">Here</At>
      <At x={36} base={659.78} size={8}>Keep a copy for</At>
      <At x={36} base={669.38} size={8}>your records.</At>

      <At x={112} base={623.16} w={466}
        size={7}
        nowrap={false}
        leading={8.4}
        style={{ textAlign: "justify" }}
      >
        Under penalties of perjury, I (applicant/delegate/acceptance agent)
        declare that I have examined this application, including accompanying
        documentation and statements, and to the best of my knowledge and
        belief, it is true, correct, and complete. I authorize the IRS to share
        information with my acceptance agent in order to perfect this Form W-7,
        Application for IRS Individual Taxpayer Identification Number.
      </At>

      <Marker x={116.78} y={648.0} size={24.08} />
      <At x={129.6} base={655.71} size={8}>Signature of applicant (if delegate, see instructions)</At>
      <At x={342.4} base={655.71} tz={0.9} size={8}>Date (month / day / year)</At>
      <At x={370.9} base={669.71} size={8}>/</At>
      <At x={396.9} base={669.71} size={8}>/</At>
      <At x={436} base={655.71} size={8}>Phone number</At>
      <Field x={436} base={668.0} w={138} h={10} value={signaturePhone} onChange={set("signaturePhone")} disabled={disabled} />

      <Marker x={116.78} y={672.0} size={24.08} />
      <At x={129.6} base={679.71} size={8}>Name of delegate, if applicable (type or print)</At>
      <At x={342.4} base={679.71} tz={0.92} size={8}>Delegate’s relationship</At>
      <At x={342.4} base={689.31} tz={0.92} size={8}>to applicant</At>
      <Marker x={423.0} y={673.3} size={21.41} />
      <Cb x={434} y={674} checked={v?.delegate === "parent"} />
      <At x={445.4} base={681.71} size={8}>Parent</At>
      <Cb x={477.2} y={674} checked={v?.delegate === "guardian"} />
      <At x={488.6} base={681.71} tz={0.945} size={8}>Court-appointed guardian</At>
      <Cb x={434} y={686} checked={v?.delegate === "poa"} />
      <At x={445.4} base={693.21} size={8}>Power of attorney</At>

      {/* ================= Acceptance Agent's Use ONLY ===================== */}
      <At x={36} base={710.86} size={10} face="demi">Acceptance</At>
      <At x={36} base={722.86} size={10} face="demi">Agent’s</At>
      <At x={36} base={734.86} size={10} face="demi">Use ONLY</At>

      <Marker x={117.0} y={697.3} size={21.41} />
      <At x={129.6} base={703.71} size={8}>Signature</At>
      <At x={342.4} base={703.71} tz={0.9} size={8}>Date (month / day / year)</At>
      <At x={370.9} base={717.71} size={8}>/</At>
      <At x={396.9} base={717.71} size={8}>/</At>
      <At x={436} base={705.21} size={8}>Phone</At>
      <At x={436} base={717.21} size={8}>Fax</At>
      <Field x={470} base={705.0} w={104} h={10} value={signaturePhone} onChange={set("signaturePhone")} disabled={disabled} />
      <Field x={470} base={717.0} w={104} h={10} value={v?.signatureFax ?? "844/8217449"} onChange={set("signatureFax")} disabled={disabled} />

      <Marker x={117.0} y={721.3} size={21.41} />
      <At x={129.6} base={727.71} size={8}>Name and title (type or print)</At>
      <Field x={129.6} base={739.0} w={162} h={10} value={v?.nameTitle ?? "TheITIN.com"} onChange={set("nameTitle")} disabled={disabled} />
      <At x={299.2} base={727.71} size={8}>Name of company</At>
      <Field x={299.2} base={739.0} w={100} h={10} value={v?.companyName ?? "Lygal INC"} onChange={set("companyName")} disabled={disabled} />
      <At x={407.2} base={729.21} size={8}>EIN</At>
      <At x={493.6} base={729.21} size={8}>PTIN</At>
      <Field x={513} base={729.0} w={62} h={10} value={v?.ptin ?? "P01086627"} onChange={set("ptin")} disabled={disabled} />
      <At x={407.2} base={741.21} size={8}>Office code</At>

      {/* ================= footer ========================================== */}
      <At x={36} base={753.71} size={8} bold>
        For Paperwork Reduction Act Notice, see separate instructions.
      </At>
      <At x={359.6} base={754.0} size={7}>Cat. No. 10229L</At>
      <At x={494.9} base={753.64} size={7}>
        Form <b style={{ fontSize: pt(10) }}>W-7</b> (Rev. 8-2019)
      </At>
    </div>
  );
};

export default W7Page;
